import styled from 'styled-components';
import Link from 'next/link';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY } from '@/constants/typography';
import { media } from '@/constants/breakpoints';

export interface BreadcrumbItem {
  label: string;
  /** Omit for the current page, or for an unlinked category label (e.g. "Universal Filters"). */
  href?: string;
}

interface BreadcrumbsProps {
  /** The chain after Home. Home -> "/" is always prepended automatically. */
  items: BreadcrumbItem[];
}

export default function Breadcrumbs({ items }: BreadcrumbsProps) {
  const allItems: BreadcrumbItem[] = [{ label: 'Home', href: '/' }, ...items];

  return (
    <Nav aria-label="Breadcrumb">
      <List>
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          return (
            <Item key={`${item.label}-${index}`}>
              {item.href && !isLast ? (
                <Crumb href={item.href}>{item.label}</Crumb>
              ) : (
                <Current aria-current={isLast ? 'page' : undefined}>{item.label}</Current>
              )}
              {!isLast && <Separator aria-hidden="true">/</Separator>}
            </Item>
          );
        })}
      </List>
    </Nav>
  );
}

const Nav = styled.nav`
  /* Full-bleed breakout: some pages nest Breadcrumbs inside .container-0
     (which adds its own 5% side margin on desktop), others render it
     directly in a plain <main> with no side margin at all. Breaking out to
     the full viewport width here — regardless of how deeply/loosely the
     parent already indented it — is what makes the breadcrumb sit at the
     exact same pixel position on every page. (Safe: app/globals.css already
     sets overflow-x: hidden site-wide, so 100vw can't cause a horizontal
     scrollbar.) The fixed-px padding below (not %) then provides the actual
     edge spacing, identically at every breakpoint.
     Breadcrumbs must always be the first element inside <main>, directly
     after <div className="header-gap-creator">, for the top offset below
     to correctly clear the fixed header stack. */
  width: 100vw;
  margin-left: calc(50% - 50vw);
  /* Measured against the live fixed header: on desktop the promo-bar +
     nav stack's real bottom edge is ~112px from the top, but
     .header-gap-creator only reserves 67px there — a ~45px shortfall.
     56px of margin (on top of the 14px padding-top below) clears that
     with a safety buffer. At <=768px, .header-gap-creator switches to a
     much taller 110px bucket that already clears the (shorter, ~121px)
     mobile header stack on its own, so 56px there is pure excess —
     reduced below to keep the top gap looking intentional, not oversized. */
  margin-top: 56px;
  padding: 14px 20px;

  ${media.tablet} {
    margin-top: 16px;
  }
  /* Some hero sections (e.g. Cleaner & Care) use a decorative image with a
     large negative offset that intentionally bleeds above its own container
     for visual effect. Since that image is position:absolute, it paints
     above plain in-flow content regardless of DOM order or vertical gap.
     Giving the breadcrumb its own stacking context keeps it on top of any
     such content, on every page, without having to special-case each one. */
  position: relative;
  z-index: 2;

  ${media.mobile} {
    padding: 10px 16px;
  }
`;

const List = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Crumb = styled(Link)`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.brandGreenAccessible};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  ${media.mobile} {
    font-size: 13px;
  }
`;

const Current = styled.span`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textPrimary};
  font-weight: 600;

  ${media.mobile} {
    font-size: 13px;
  }
`;

const Separator = styled.span`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};

  ${media.mobile} {
    font-size: 13px;
  }
`;
